import { forwardRef, useRef } from 'react'
import { Calendar } from 'lucide-react'
import { Input, type InputProps } from '@/components/ui/input'
import { maskDateInput } from '@/lib/utils'
import { cn } from '@/lib/utils'

export interface DateInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
}

function toIsoDate(dotted: string): string {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(dotted)) return ''
  const [dd, mm, yyyy] = dotted.split('.')
  return `${yyyy}-${mm}-${dd}`
}

function fromIsoDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
  const [yyyy, mm, dd] = iso.split('-')
  return `${dd}.${mm}.${yyyy}`
}

/** Текстовый ввод ДД.ММ.ГГГГ + кнопка нативного календаря. */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onChange, placeholder = 'ДД.ММ.ГГГГ', className, disabled, ...props }, ref) => {
    const pickerRef = useRef<HTMLInputElement>(null)
    const isoValue = toIsoDate(value)

    const openPicker = () => {
      const el = pickerRef.current
      if (!el || disabled) return
      try {
        el.showPicker()
      } catch {
        el.click()
      }
    }

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          maxLength={10}
          value={value}
          disabled={disabled}
          className={cn('pr-10', className)}
          onChange={(e) => onChange(maskDateInput(e.target.value))}
          {...props}
        />
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden
          disabled={disabled}
          value={isoValue}
          onChange={(e) => {
            const next = fromIsoDate(e.target.value)
            if (next) onChange(next)
          }}
          className="pointer-events-none absolute h-0 w-0 opacity-0"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Открыть календарь"
          onClick={openPicker}
          className={cn(
            'absolute right-2 top-2 rounded p-0.5 text-gray-500 hover:text-gray-800',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>
    )
  },
)
DateInput.displayName = 'DateInput'
